import { createApiClient, getLocalTimestamp, getTimezoneOffsetString } from '../../../../../api/apiClient';
import { buildStandardFilterRequest } from '../../../utils/filterBuilder';
import type { AppliedFiltersSnapshot } from '../../../types/filterTypes';

// locationId/locType/year/period/week are independent multi-select arrays;
// the backend resolves overlap itself, so no fiscalSelections cartesian expansion is built here.
export interface KpiExecutiveOverviewRequest {
  locationId?: string[];
  locType?: string[];
  startDate?: string | null;
  endDate?: string | null;
  year?: number[];
  period?: number[];
  week?: number[];
}

export interface KpiExecutiveOverviewResponse {
  completedPercentage: number;
  onTimePercentage: number;
  incompleteLoads: number;
  driverArrivedAsExpectedPercentage: number;
  plannedLoads: number;
  completedLoads: number;
  onTimeLoads: number;
  lateLoads: number;
  driverArrivedAsExpectedCount?: number;
  driverArrivalOpportunities?: number;
  totalDrivers?: number;
  completedSscc?: number;
  requestedSscc?: number;
  completePerLoadPercentage?: number;
  driverArrivedCount?: number;
  driverManifestCount?: number;
}

/**
 * Normalizes KPI response to ensure all fields have valid values.
 */
export function normalizeKpiResponse(
  data: KpiExecutiveOverviewResponse
): KpiExecutiveOverviewResponse {
  return {
    completedPercentage: data.completedPercentage ?? 0,
    onTimePercentage: data.onTimePercentage ?? 0,
    incompleteLoads: data.incompleteLoads ?? 0,
    driverArrivedAsExpectedPercentage: data.driverArrivedAsExpectedPercentage ?? 0,
    plannedLoads: data.plannedLoads ?? 0,
    completedLoads: data.completedLoads ?? 0,
    onTimeLoads: data.onTimeLoads ?? 0,
    lateLoads: data.lateLoads ?? 0,
    driverArrivedAsExpectedCount: data.driverArrivedAsExpectedCount ?? data.driverArrivedCount ?? 0,
    driverArrivalOpportunities: data.driverArrivalOpportunities ?? data.driverManifestCount ?? 0,
    totalDrivers: data.totalDrivers ?? 0,
    completedSscc: data.completedSscc ?? 0,
    requestedSscc: data.requestedSscc ?? 0,
    completePerLoadPercentage: data.completePerLoadPercentage ?? 0,
    driverArrivedCount: data.driverArrivedCount,
    driverManifestCount: data.driverManifestCount,
  };
}

// Extracts a compact string-id array from a dropdown selection list, or undefined if empty.
const toIdArray = (items?: { id: string | number; displayText: string }[]): string[] | undefined => {
  if (!items || items.length === 0) return undefined;
  const ids = items.map((item) => String(item.id).trim()).filter(Boolean);
  return ids.length > 0 ? ids : undefined;
};

const normalizeFilterValue = (value: unknown): string =>
  String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

// Mirrors Load Execution's location resolution: when nothing is selected, this enumerates
// every visible location ID instead of omitting the filter, matching the scorecards payload shape.
function resolveVisibleLocationIds(
  appliedFilters: AppliedFiltersSnapshot,
  adminLocations: any[]
): string[] {
  const explicit = new Set(
    appliedFilters.selectedLocations.map((l) => String(l.id).trim()).filter(Boolean)
  );
  if (explicit.size > 0) return Array.from(explicit);

  const regionIds = new Set(appliedFilters.selectedRegions.map((r) => normalizeFilterValue(r.id)));
  const marketIds = new Set(appliedFilters.selectedMarkets.map((m) => normalizeFilterValue(m.id)));

  const resolved = new Set<string>();
  adminLocations.forEach((loc) => {
    const regionLabel = normalizeFilterValue(loc?.region ?? loc?.unit ?? '');
    const marketLabel = normalizeFilterValue(
      loc?.market ?? loc?.pod ?? loc?.marketPod ?? loc?.marketName ?? ''
    );
    // `locationId` (EnrichedLocation, from appData.locations) must win over any
    // name-like field — otherwise a location whose `locationId` is falsy/0 but
    // has a `locName`/`name` would incorrectly resolve to its display name
    // instead of its id. `locId` is kept only for back-compat with any caller
    // still passing AdminLocation-shaped entries.
    const locId = String(
      loc?.locId ?? loc?.locationId ?? ''
    ).trim();
    const regionMatches = regionIds.size === 0 || regionIds.has(regionLabel);
    const marketMatches = marketIds.size === 0 || marketIds.has(marketLabel);
    if (regionMatches && marketMatches && locId) resolved.add(locId);
  });

  return Array.from(resolved);
}

/**
 * Fetches KPI Executive Overview using standardized filter builder.
 * Converts frontend filter format to backend API contract.
 */
export async function fetchKpiExecutiveOverview(
  appliedFilters: AppliedFiltersSnapshot,
  adminLocations: any[]
): Promise<KpiExecutiveOverviewResponse> {
  // Resolve region/market/location selections to concrete location IDs client-side,
  // matching Load Execution's pattern — the backend does not reliably filter by raw market strings.
  const standard = buildStandardFilterRequest(appliedFilters, []);
  const resolvedLocationIds = resolveVisibleLocationIds(appliedFilters, adminLocations);
  const locType = toIdArray(appliedFilters.selectedLocationTypes);

  const backendRequest: KpiExecutiveOverviewRequest = {
    ...(resolvedLocationIds.length > 0 && { locationId: resolvedLocationIds }),
    ...(locType && { locType }),
    startDate: standard.deliveryDateStart ?? null,
    endDate: standard.deliveryDateEnd ?? null,
    ...(standard.year && standard.year.length > 0 && { year: standard.year }),
    ...(standard.period && standard.period.length > 0 && { period: standard.period }),
    ...(standard.week && standard.week.length > 0 && { week: standard.week }),
  };

  console.log('[KPI API] Built request:', JSON.stringify(backendRequest, null, 2));

  const api = createApiClient();
  const clientTimestamp = getLocalTimestamp();
  const clientTimezone = getTimezoneOffsetString();

  try {
    const response = await api.post<{ data: KpiExecutiveOverviewResponse }>(
      '/ontime-dispatch/executive-overview',
      backendRequest,
      { params: { clientTimestamp, clientTimezone } }
    );

    console.log('[KPI API] Full response received:', response.data);

    const innerData = response.data?.data || response.data;
    const normalizedData = normalizeKpiResponse(innerData as KpiExecutiveOverviewResponse);

    console.log('[KPI API] Normalized response:', normalizedData);
    return normalizedData;
  } catch (error) {
    console.error('[KPI API] Error response:', error);
    throw error;
  }
}
